import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { WhiteBox } from 'components/flink-play';
import SupportPhone from 'components/common/SupportPhone';
import { StarIcon } from 'components/flink-play/FlinkPlayIcons/FlinkPlayIcons';
import { isMobileAppRuntime } from 'utils';
import classes from './MenuGroupView.module.scss';

const IKTYW = 'I Know These Words';
const FLC_LOGO_URL = 'https://s3.us-east-2.amazonaws.com/flink-2.0-fs/Files/LandingPageAssets/Logo/Family_Logo_2024.svg';

const missingAssessmentLabelWarnings = new Set();

const toKey = (value) => (value === null || value === undefined ? null : String(value));

const addResultMapping = (acc, key, result) => {
  const normalizedKey = toKey(key);
  if (normalizedKey && result && !acc[normalizedKey]) acc[normalizedKey] = result;
};

const buildAssessmentResultsBySourceId = (assessmentAttempt) => {
  const results = assessmentAttempt?.activityResults || [];
  const expectedKeys = assessmentAttempt?.expectedActivityKeys || [];
  const leafIds = assessmentAttempt?.assessmentLeafActivityIds || [];

  return results.reduce((acc, result) => {
    addResultMapping(acc, result?.sourceActivityId, result);
    addResultMapping(acc, result?.activityId, result);
    addResultMapping(acc, result?.meta?.sourceActivityId, result);

    if (result?.activityKey) {
      const keyIndex = expectedKeys.findIndex((key) => key === result.activityKey);
      if (keyIndex >= 0) addResultMapping(acc, leafIds[keyIndex], result);
    }

    return acc;
  }, {});
};


const buildAssessmentMenuBySourceId = (assessmentAttempt) => {
  const menu = assessmentAttempt?.assessmentMenu || [];
  const expectedKeys = assessmentAttempt?.expectedActivityKeys || [];
  const leafIds = assessmentAttempt?.assessmentLeafActivityIds || [];

  return menu.reduce((acc, item) => {
    addResultMapping(acc, item?.sourceActivityId, item);
    addResultMapping(acc, item?.activityId, item);

    if (item?.activityKey) {
      addResultMapping(acc, item.activityKey, item);
      const keyIndex = expectedKeys.findIndex((key) => key === item.activityKey);
      if (keyIndex >= 0) addResultMapping(acc, leafIds[keyIndex], item);
    }

    return acc;
  }, {});
};

const buildAssessmentFeedback = (result) => ({
  mastered: result?.star === 'green' || result?.mastered === true,
  partial: result?.star === 'yellow' || result?.partial === true,
});

const getItemLookupKeys = (item) => ([
  item?._id,
  item?.id,
  item?.activityId,
  item?.sourceActivityId,
  item?.activity?._id,
  item?.element?._id,
]).map(toKey).filter(Boolean);

const findAssessmentResultForItem = (item, resultsBySourceId, assessmentAttempt, itemIndex) => {
  if (item?.__assessmentMenuState?.activityKey) {
    return (assessmentAttempt?.activityResults || [])
      .find((result) => result?.activityKey === item.__assessmentMenuState.activityKey) || null;
  }

  const keys = getItemLookupKeys(item);

  for (const key of keys) {
    if (resultsBySourceId[key]) return resultsBySourceId[key];
  }

  const expectedKey = assessmentAttempt?.expectedActivityKeys?.[itemIndex];
  if (expectedKey) {
    return (assessmentAttempt?.activityResults || [])
      .find((result) => result?.activityKey === expectedKey) || null;
  }

  return null;
};

const findAssessmentMenuStateForItem = (item, menuBySourceId, assessmentAttempt, itemIndex) => {
  if (item?.__assessmentMenuState) return item.__assessmentMenuState;

  const keys = getItemLookupKeys(item);

  for (const key of keys) {
    if (menuBySourceId[key]) return menuBySourceId[key];
  }

  const expectedKey = assessmentAttempt?.expectedActivityKeys?.[itemIndex];
  if (expectedKey) {
    return (assessmentAttempt?.assessmentMenu || [])
      .find((menuItem) => menuItem?.activityKey === expectedKey) || null;
  }

  return null;
};


const buildAssessmentItemsInSlotOrder = (baseItems, assessmentAttempt) => {
  const menu = Array.isArray(assessmentAttempt?.assessmentMenu) ? assessmentAttempt.assessmentMenu : [];

  if (!menu.length) return baseItems;

  const itemsById = baseItems.reduce((acc, item) => {
    getItemLookupKeys(item).forEach((key) => {
      if (!acc[key]) acc[key] = item;
    });
    return acc;
  }, {});

  return menu.map((menuState, index) => {
    const sourceId = toKey(menuState?.sourceActivityId);
    const matchedItem = sourceId ? itemsById[sourceId] : null;

    return {
      ...(matchedItem || {}),
      _id: matchedItem?._id || sourceId || menuState?.activityKey || `assessment-slot-${index + 1}`,
      isGroup: false,
      formattedName: matchedItem?.formattedName || matchedItem?.activityName || menuState?.sourceActivityName || '',
      __assessmentMenuState: menuState,
      __assessmentSlotIndex: index,
    };
  });
};

const getAssessmentItemLabel = (item, menuState) => {
  const actualActivityName = menuState?.sourceActivityName || item?.activityName || item?.formattedName || '';

  if (actualActivityName) return actualActivityName;
  if (menuState?.label) return menuState.label;

  const warningKey = toKey(menuState?.sourceActivityId || item?._id || menuState?.activityKey);

  if (warningKey && !missingAssessmentLabelWarnings.has(warningKey)) {
    missingAssessmentLabelWarnings.add(warningKey);
    console.error('[ASSESSMENT][menu][missing-label]', {
      activityKey: menuState?.activityKey || null,
      sourceActivityId: menuState?.sourceActivityId || item?._id || null,
      sourceActivityName: null,
    });
  }

  return '';
};

const renderAssessmentLegend = () => (
  <ul className={classes.assessmentLegend}>
    <li>
      <span className={classes.assessmentLegendStar}>
        <StarIcon partial />
      </span>
      <span>Partial</span>
    </li>
    <li>
      <span className={classes.assessmentLegendStar}>
        <StarIcon mastered />
      </span>
      <span>Mastered</span>
    </li>
  </ul>
);

const MenuGroupView = ({
  title,
  items,
  learners,
  highlight,
  withoutStars,
  activityClickHandler,
  groupClickHandler,
  isDiagnosticAssessment = false,
  assessmentAttempt,
}) => {
  const base = Array.isArray(items) ? items.filter(Boolean) : [];
  let listForRender = isDiagnosticAssessment
    ? buildAssessmentItemsInSlotOrder(base, assessmentAttempt)
    : base.slice();
  const assessmentResultsBySourceId = buildAssessmentResultsBySourceId(assessmentAttempt);
  const assessmentMenuBySourceId = buildAssessmentMenuBySourceId(assessmentAttempt);

  if (!isDiagnosticAssessment && listForRender.length && !listForRender[0].isGroup) {
    const idx = listForRender.findIndex((it) => {
      const fn = it?.formattedName || '';
      const an = it?.activityName || '';
      return fn === IKTYW || an === IKTYW || an.endsWith(`~${IKTYW}`);
    });

    if (idx !== -1 && idx !== listForRender.length - 1) {
      const clone = listForRender.slice();
      const [ik] = clone.splice(idx, 1);
      clone.push(ik);
      listForRender = clone;
    }
  }

  const renderedTitle = isDiagnosticAssessment ? 'Pick an activity' : title;

  return (
    <WhiteBox
      outerClass={classnames(classes.wrapper, {
        [classes.diagnosticAssessment]: isDiagnosticAssessment,
        [classes.mobileRuntime]: isMobileAppRuntime(),
      })}
      innerClass={classnames(classes.innerWrapper, {
        [classes.diagnosticAssessmentInner]: isDiagnosticAssessment,
      })}
    >
      {isDiagnosticAssessment && (
        <aside className={classes.assessmentSidePanel}>
          <div className={classes.assessmentLogoWrapper}>
            <img src={FLC_LOGO_URL} alt="Family Learning Company" />
          </div>
          {renderAssessmentLegend()}
          <div className={classes.assessmentSupport}>
            <SupportPhone />
          </div>
        </aside>
      )}

      <div className={classes.assessmentContent}>
        {renderedTitle && <h2 className={classes.title}>{renderedTitle}</h2>}

        <ul className={classes.itemsList}>
        {listForRender.map((ele, itemIndex) => {
          const { _id, isGroup } = ele;
          const clickHandler = isGroup ? groupClickHandler : activityClickHandler;
          const assessmentResult = isDiagnosticAssessment && !isGroup
            ? findAssessmentResultForItem(ele, assessmentResultsBySourceId, assessmentAttempt, itemIndex)
            : null;
          const assessmentMenuState = isDiagnosticAssessment && !isGroup
            ? findAssessmentMenuStateForItem(ele, assessmentMenuBySourceId, assessmentAttempt, itemIndex)
            : null;
          const isLocked = assessmentMenuState?.status === 'locked';
          const learnerFeedbacks = (learners || []).map((learner) => {
            const report = learner.reports && learner.reports[_id];
            return report || {};
          });
          const feedbacks = isDiagnosticAssessment && !isGroup
            ? [assessmentResult ? buildAssessmentFeedback(assessmentResult) : (learnerFeedbacks[0] || {})]
            : learnerFeedbacks;

          return (
            <li key={_id}>
              <button
                onClick={() => { if (!isLocked && clickHandler) clickHandler(_id); }}
                disabled={isLocked}
                className={classnames(classes.item, {
                  [classes.clickable]: !!clickHandler && !isLocked,
                  [classes.itemGroup]: isGroup,
                  [classes.itemActivity]: !isGroup,
                  [classes.previous]: !isGroup && _id === highlight,
                  [classes.locked]: isLocked,
                })}
              >
                {withoutStars && !isDiagnosticAssessment
                  ? null
                  : feedbacks.map((f, idx) => (
                      <div key={idx}>
                        <StarIcon
                          idx={idx}
                          mastered={!!f.mastered}
                          partial={!!f.partial}
                        />
                      </div>
                    ))}

                <span className={classes.itemLabel}>
                  {isDiagnosticAssessment
                    ? getAssessmentItemLabel(ele, assessmentMenuState)
                    : ele.formattedName}
                </span>
              </button>
            </li>
          );
        })}
        </ul>
      </div>
    </WhiteBox>
  );
};

MenuGroupView.propTypes = {
  withoutStars: PropTypes.bool,
  title: PropTypes.node,
  items: PropTypes.array.isRequired,
  activityClickHandler: PropTypes.func,
  groupClickHandler: PropTypes.func,
  learners: PropTypes.array,
  highlight: PropTypes.string,
  isDiagnosticAssessment: PropTypes.bool,
  assessmentAttempt: PropTypes.object,
};

MenuGroupView.defaultProps = {
  isDiagnosticAssessment: false,
};

export default MenuGroupView;
